import React from 'react';
import { AbsoluteFill, Sequence, useCurrentFrame, interpolate, spring, useVideoConfig } from 'remotion';

// 化学模板专业版 - H₂ + O₂ → H₂O 反应（横屏 1920×1080）
const THEME = {
  primary: '#00B894',
  primaryLight: '#55EFC4',
  accent: '#FF6B6B',
  oxygen: '#E74C3C',
  hydrogen: '#3498DB',
  highlight: '#FFB347',
  bg: 'linear-gradient(135deg, #E8F8F3 0%, #DFF5EB 50%, #FFFFFF 100%)',
  cardShadow: '0 16px 40px rgba(0, 184, 148, 0.16)',
};

export const ChemistryDemoPro: React.FC = () => {
  return (
    <AbsoluteFill style={{ background: THEME.bg }}>
      <DecorativeBackground />

      <Sequence from={0} durationInFrames={150}>
        <IntroScene />
      </Sequence>
      <Sequence from={150} durationInFrames={150}>
        <TitleScene />
      </Sequence>
      <Sequence from={300} durationInFrames={1050}>
        <ExplanationScene />
      </Sequence>
      <Sequence from={1350} durationInFrames={300}>
        <QuizScene />
      </Sequence>
      <Sequence from={1650} durationInFrames={150}>
        <OutroScene />
      </Sequence>
    </AbsoluteFill>
  );
};

const DecorativeBackground: React.FC = () => {
  const frame = useCurrentFrame();
  return (
    <AbsoluteFill>
      {[
        { x: 140, y: 220, r: 150, c: THEME.primary, o: 0.06 },
        { x: 1720, y: 260, r: 200, c: THEME.hydrogen, o: 0.05 },
        { x: 280, y: 880, r: 200, c: THEME.oxygen, o: 0.05 },
        { x: 1680, y: 860, r: 140, c: THEME.highlight, o: 0.06 },
      ].map((c, i) => {
        const float = Math.sin((frame + i * 35) * 0.028) * 12;
        return (
          <div
            key={i}
            style={{
              position: 'absolute',
              left: c.x - c.r,
              top: c.y - c.r + float,
              width: c.r * 2,
              height: c.r * 2,
              borderRadius: '50%',
              background: c.c,
              opacity: c.o,
              filter: 'blur(22px)',
            }}
          />
        );
      })}
    </AbsoluteFill>
  );
};

const IntroScene: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const scale = spring({ frame, fps, config: { damping: 12, stiffness: 120 } });
  const bubble = Math.sin(frame * 0.15) * 10;
  const fadeText = interpolate(frame, [20, 50], [0, 1], { extrapolateRight: 'clamp' });
  const slideY = interpolate(frame, [20, 50], [30, 0], { extrapolateRight: 'clamp' });

  return (
    <AbsoluteFill style={{ justifyContent: 'center', alignItems: 'center', flexDirection: 'row', gap: 80 }}>
      <div style={{ fontSize: 240, transform: `scale(${scale}) translateY(${bubble}px)` }}>🧪</div>
      <div style={{ opacity: fadeText, transform: `translateY(${slideY}px)` }}>
        <div style={{ fontSize: 120, fontWeight: 900, color: THEME.primary, letterSpacing: 4 }}>化学</div>
        <div style={{ fontSize: 32, color: '#555', marginTop: 16, letterSpacing: 6 }}>
          CHEMISTRY · 物质变化
        </div>
      </div>
    </AbsoluteFill>
  );
};

const TitleScene: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const scale = spring({ frame, fps, config: { damping: 14, stiffness: 110 } });
  const lineWidth = interpolate(frame, [30, 80], [0, 480], { extrapolateRight: 'clamp' });

  return (
    <AbsoluteFill style={{ justifyContent: 'center', alignItems: 'center' }}>
      <div
        style={{
          padding: '72px 120px',
          background: '#fff',
          borderRadius: 32,
          boxShadow: THEME.cardShadow,
          transform: `scale(${scale})`,
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: 26, color: THEME.primary, letterSpacing: 8, marginBottom: 16 }}>
          CHAPTER · 03
        </div>
        <div style={{ fontSize: 68, fontWeight: 900, color: THEME.primary, marginBottom: 24 }}>
          化学方程式配平
        </div>
        <div style={{ fontSize: 36, color: '#333', fontWeight: 600, fontFamily: 'Georgia, serif' }}>
          2H₂ + O₂ → 2H₂O
        </div>
        <div
          style={{
            height: 4,
            width: lineWidth,
            background: `linear-gradient(90deg, ${THEME.hydrogen}, ${THEME.oxygen})`,
            margin: '24px auto 0',
            borderRadius: 2,
          }}
        />
      </div>
    </AbsoluteFill>
  );
};

const ExplanationScene: React.FC = () => {
  const frame = useCurrentFrame();

  return (
    <AbsoluteFill style={{ padding: 60, flexDirection: 'row', gap: 40, alignItems: 'center' }}>
      {/* 左侧：反应可视化 */}
      <div
        style={{
          width: 1080,
          height: 820,
          background: '#fff',
          borderRadius: 24,
          position: 'relative',
          boxShadow: THEME.cardShadow,
          overflow: 'hidden',
        }}
      >
        <div style={{ padding: '28px 36px', borderBottom: '1px solid #E8F8F3' }}>
          <div style={{ fontSize: 32, fontWeight: 700, color: THEME.primary }}>
            分子级反应 · 质量守恒可视化
          </div>
          <div style={{ fontSize: 26, color: '#888', marginTop: 6 }}>
            氢气与氧气燃烧生成水，原子数守恒
          </div>
        </div>
        <ReactionAnimation frame={frame} />
      </div>

      {/* 右侧：要点 */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div style={{ fontSize: 26, color: THEME.primary, letterSpacing: 4, fontWeight: 600 }}>
          KNOWLEDGE POINTS
        </div>
        <div style={{ fontSize: 42, fontWeight: 900, color: '#1a1a1a', lineHeight: 1.2, marginBottom: 12 }}>
          配平三步法轻松掌握
        </div>

        {[
          { step: '01', title: '质量守恒', content: '反应前后原子种类和数量保持不变', color: THEME.primary },
          { step: '02', title: '观察法', content: '从复杂分子入手，逐步调整化学计量数', color: THEME.hydrogen },
          { step: '03', title: '最小公倍数', content: '找原子数的最小公倍数完成配平', color: THEME.oxygen },
        ].map((point, i) => {
          const start = i * 200 + 100;
          const opacity = interpolate(frame, [start, start + 30], [0, 1], { extrapolateRight: 'clamp' });
          const x = interpolate(frame, [start, start + 30], [40, 0], { extrapolateRight: 'clamp' });

          return (
            <div
              key={i}
              style={{
                padding: '22px 28px',
                background: '#fff',
                borderRadius: 16,
                opacity,
                transform: `translateX(${x}px)`,
                boxShadow: '0 4px 16px rgba(0, 184, 148, 0.08)',
                borderLeft: `4px solid ${point.color}`,
                display: 'flex',
                alignItems: 'center',
                gap: 20,
              }}
            >
              <div
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: 16,
                  background: `linear-gradient(135deg, ${point.color}, ${point.color}AA)`,
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 30,
                  fontWeight: 900,
                  flexShrink: 0,
                }}
              >
                {point.step}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 26, fontWeight: 700, color: '#222', marginBottom: 4 }}>
                  {point.title}
                </div>
                <div style={{ fontSize: 22, color: '#666' }}>{point.content}</div>
              </div>
            </div>
          );
        })}
      </div>
    </AbsoluteFill>
  );
};

// 原子圆（带立体高光）
const Atom: React.FC<{ cx: number; cy: number; r: number; color: string; label: string; labelSize?: number }> = ({
  cx, cy, r, color, label, labelSize = 22,
}) => (
  <g>
    <circle cx={cx + r * 0.15} cy={cy + r * 0.2} r={r} fill="#000" opacity={0.12} />
    <circle cx={cx} cy={cy} r={r} fill={color} />
    <circle cx={cx - r * 0.3} cy={cy - r * 0.3} r={r * 0.35} fill="#fff" opacity={0.35} />
    <text x={cx} y={cy + labelSize / 3} fontSize={labelSize} fill="#fff" fontWeight="900" textAnchor="middle">
      {label}
    </text>
  </g>
);

const ReactionAnimation: React.FC<{ frame: number }> = ({ frame }) => {
  const width = 1080;
  const height = 720;

  // 阶段：
  // 0-150: 反应前展示（2个 H₂ + 1个 O₂）
  // 150-350: 分子向中心移动（反应进行）
  // 350-500: 形成 2个 H₂O
  // 500-1050: 稳定展示 + 方程式高亮

  const approach = interpolate(frame, [150, 350], [0, 1], { extrapolateRight: 'clamp' });
  const rearrange = interpolate(frame, [350, 500], [0, 1], { extrapolateRight: 'clamp' });
  const equationProg = interpolate(frame, [20, 60], [0, 1], { extrapolateRight: 'clamp' });
  const glow = interpolate(frame, [300, 400, 500], [0, 1, 0], { extrapolateRight: 'clamp' });

  const leftCenterX = 220;
  const rightCenterX = 860;
  const centerY = 380;

  // H₂ 分子 1（上左）：两个 H
  const h2_1_cx = interpolate(approach, [0, 1], [leftCenterX - 80, 440]);
  const h2_1_cy = interpolate(approach, [0, 1], [centerY - 140, centerY - 80]);
  // H₂ 分子 2（下左）
  const h2_2_cx = interpolate(approach, [0, 1], [leftCenterX - 80, 440]);
  const h2_2_cy = interpolate(approach, [0, 1], [centerY + 140, centerY + 80]);
  // O₂ 分子（中间）
  const o2_cx = interpolate(approach, [0, 1], [leftCenterX + 220, 440 + 150]);
  const o2_cy = centerY;

  // 反应后：2 个 H₂O 分子在右侧
  const water1_cx = rightCenterX - 60;
  const water1_cy = centerY - 110;
  const water2_cx = rightCenterX + 60;
  const water2_cy = centerY + 110;

  // 水分子位置：从中心移向右侧
  const waterSpawn = interpolate(frame, [400, 500], [0, 1], { extrapolateRight: 'clamp' });
  const w1x = interpolate(waterSpawn, [0, 1], [440, water1_cx]);
  const w1y = interpolate(waterSpawn, [0, 1], [centerY - 30, water1_cy]);
  const w2x = interpolate(waterSpawn, [0, 1], [550, water2_cx]);
  const w2y = interpolate(waterSpawn, [0, 1], [centerY + 30, water2_cy]);

  const reactantOpacity = interpolate(rearrange, [0, 0.6], [1, 0]);
  const productOpacity = interpolate(rearrange, [0.4, 1], [0, 1]);

  // 左侧箭头 / 右侧提示
  const arrowProg = interpolate(frame, [250, 320], [0, 1], { extrapolateRight: 'clamp' });

  return (
    <svg width={width} height={height} style={{ display: 'block' }}>
      <defs>
        <pattern id="chem-grid" width="60" height="60" patternUnits="userSpaceOnUse">
          <path d="M 60 0 L 0 0 0 60" fill="none" stroke="#E8F8F3" strokeWidth="1" />
        </pattern>
        <radialGradient id="flash" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0%" stopColor={THEME.highlight} stopOpacity="0.7" />
          <stop offset="100%" stopColor={THEME.highlight} stopOpacity="0" />
        </radialGradient>
      </defs>

      <rect width={width} height={height} fill="url(#chem-grid)" />

      {/* 反应分区线（左：反应物；右：生成物） */}
      <line x1={width / 2} y1={80} x2={width / 2} y2={height - 120} stroke="#E0EEE9" strokeWidth="2" strokeDasharray="8,8" />
      <text x={width / 4} y={80} fontSize="24" fill={THEME.primary} fontWeight="700" textAnchor="middle" letterSpacing="3">
        反应物 · REACTANTS
      </text>
      <text x={(width * 3) / 4} y={80} fontSize="24" fill={THEME.primary} fontWeight="700" textAnchor="middle" letterSpacing="3">
        生成物 · PRODUCTS
      </text>

      {/* 反应闪光 */}
      {glow > 0 && (
        <circle cx={500} cy={centerY} r={140} fill="url(#flash)" opacity={glow} />
      )}

      {/* 反应物：2 H₂ + O₂ */}
      <g opacity={reactantOpacity}>
        {/* H₂ 分子 1 */}
        <line x1={h2_1_cx - 40} y1={h2_1_cy} x2={h2_1_cx + 40} y2={h2_1_cy} stroke="#666" strokeWidth="5" />
        <Atom cx={h2_1_cx - 40} cy={h2_1_cy} r={32} color={THEME.hydrogen} label="H" labelSize={24} />
        <Atom cx={h2_1_cx + 40} cy={h2_1_cy} r={32} color={THEME.hydrogen} label="H" labelSize={24} />

        {/* H₂ 分子 2 */}
        <line x1={h2_2_cx - 40} y1={h2_2_cy} x2={h2_2_cx + 40} y2={h2_2_cy} stroke="#666" strokeWidth="5" />
        <Atom cx={h2_2_cx - 40} cy={h2_2_cy} r={32} color={THEME.hydrogen} label="H" labelSize={24} />
        <Atom cx={h2_2_cx + 40} cy={h2_2_cy} r={32} color={THEME.hydrogen} label="H" labelSize={24} />

        {/* O₂ 分子（双键） */}
        <line x1={o2_cx - 52} y1={o2_cy - 8} x2={o2_cx + 52} y2={o2_cy - 8} stroke="#666" strokeWidth="5" />
        <line x1={o2_cx - 52} y1={o2_cy + 8} x2={o2_cx + 52} y2={o2_cy + 8} stroke="#666" strokeWidth="5" />
        <Atom cx={o2_cx - 52} cy={o2_cy} r={46} color={THEME.oxygen} label="O" labelSize={30} />
        <Atom cx={o2_cx + 52} cy={o2_cy} r={46} color={THEME.oxygen} label="O" labelSize={30} />

        {approach < 0.3 && (
          <>
            <text x={leftCenterX - 80} y={centerY - 200} fontSize="20" fill={THEME.hydrogen} fontWeight="700" textAnchor="middle">
              氢气 H₂
            </text>
            <text x={leftCenterX - 80} y={centerY + 210} fontSize="20" fill={THEME.hydrogen} fontWeight="700" textAnchor="middle">
              氢气 H₂
            </text>
            <text x={leftCenterX + 220} y={centerY + 90} fontSize="20" fill={THEME.oxygen} fontWeight="700" textAnchor="middle">
              氧气 O₂
            </text>
          </>
        )}
      </g>

      {/* 反应箭头（置于中线顶端） */}
      {arrowProg > 0 && (
        <g opacity={arrowProg} transform={`translate(${width / 2 - 95}, 110)`}>
          <rect x={0} y={0} width={190} height={56} rx={28} fill="#fff" stroke={THEME.highlight} strokeWidth="2" />
          <text x={40} y={35} fontSize="22" fill={THEME.highlight} fontWeight="900">🔥</text>
          <text x={80} y={36} fontSize="22" fill={THEME.primary} fontWeight="700" textAnchor="start">
            点燃
          </text>
          <line x1={140} y1={28} x2={170} y2={28} stroke={THEME.primary} strokeWidth="4" strokeLinecap="round" />
          <polygon points="180,28 172,22 172,34" fill={THEME.primary} />
        </g>
      )}

      {/* 生成物：2 H₂O */}
      <g opacity={productOpacity}>
        {/* 水分子 1 (V 形) */}
        <WaterMolecule x={w1x} y={w1y} />
        <WaterMolecule x={w2x} y={w2y} />

        {rearrange > 0.7 && (
          <text x={rightCenterX} y={centerY + 176} fontSize="22" fill={THEME.primary} fontWeight="700" textAnchor="middle">
            水 H₂O × 2
          </text>
        )}
      </g>

      {/* 方程式底栏 */}
      <g opacity={equationProg}>
        <rect x={60} y={height - 150} width={width - 120} height={86} rx={16} fill="#fff" stroke={THEME.primary} strokeWidth="2" />
        <text x={width / 2} y={height - 100} fontSize="36" fill="#222" fontWeight="700" textAnchor="middle" fontFamily="Georgia, serif">
          <tspan fill={THEME.hydrogen} fontWeight="900">2H₂</tspan>
          <tspan> + </tspan>
          <tspan fill={THEME.oxygen} fontWeight="900">O₂</tspan>
          <tspan>  ⟶  </tspan>
          <tspan fill={THEME.primary} fontWeight="900">2H₂O</tspan>
        </text>
      </g>

      {/* 原子计数面板 */}
      {frame > 500 && (
        <g opacity={interpolate(frame, [500, 550], [0, 1], { extrapolateRight: 'clamp' })}>
          <rect x={60} y={140} width={260} height={114} rx={16} fill={THEME.primary} />
          <text x={190} y={178} fontSize="22" fill="#fff" opacity={0.85} textAnchor="middle" letterSpacing="3">
            原子守恒
          </text>
          <text x={190} y={222} fontSize="26" fill="#fff" fontWeight="700" textAnchor="middle">
            H:4  O:2  ✓
          </text>
        </g>
      )}
    </svg>
  );
};

const WaterMolecule: React.FC<{ x: number; y: number }> = ({ x, y }) => {
  const angle = 52.25; // 104.5°/2
  const bondLen = 46;
  const hx1 = x + Math.cos((-90 - angle) * Math.PI / 180) * bondLen;
  const hy1 = y + Math.sin((-90 - angle) * Math.PI / 180) * bondLen;
  const hx2 = x + Math.cos((-90 + angle) * Math.PI / 180) * bondLen;
  const hy2 = y + Math.sin((-90 + angle) * Math.PI / 180) * bondLen;
  return (
    <g>
      <line x1={x} y1={y} x2={hx1} y2={hy1} stroke="#666" strokeWidth="4" />
      <line x1={x} y1={y} x2={hx2} y2={hy2} stroke="#666" strokeWidth="4" />
      <Atom cx={x} cy={y} r={38} color={THEME.oxygen} label="O" labelSize={26} />
      <Atom cx={hx1} cy={hy1} r={22} color={THEME.hydrogen} label="H" labelSize={16} />
      <Atom cx={hx2} cy={hy2} r={22} color={THEME.hydrogen} label="H" labelSize={16} />
    </g>
  );
};

const QuizScene: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const scale = spring({ frame, fps, config: { damping: 14, stiffness: 110 } });
  const answerOpacity = interpolate(frame, [120, 160], [0, 1], { extrapolateRight: 'clamp' });
  const answerY = interpolate(frame, [120, 160], [20, 0], { extrapolateRight: 'clamp' });

  return (
    <AbsoluteFill style={{ justifyContent: 'center', alignItems: 'center' }}>
      <div
        style={{
          padding: '64px 96px',
          background: '#fff',
          borderRadius: 28,
          boxShadow: THEME.cardShadow,
          transform: `scale(${scale})`,
          minWidth: 960,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 32 }}>
          <div
            style={{
              padding: '8px 20px',
              background: THEME.primary,
              color: '#fff',
              borderRadius: 20,
              fontSize: 26,
              fontWeight: 700,
            }}
          >
            Q · 互动测验
          </div>
          <div style={{ fontSize: 26, color: '#888' }}>配平下列反应</div>
        </div>

        <div style={{ fontSize: 34, fontWeight: 700, color: '#1a1a1a', marginBottom: 40, lineHeight: 1.4 }}>
          请配平：
          <span style={{ fontFamily: 'Georgia, serif', color: THEME.primary }}> __ H₂ + __ O₂ → __ H₂O</span>
        </div>

        <div
          style={{
            opacity: answerOpacity,
            transform: `translateY(${answerY}px)`,
            padding: '28px 32px',
            background: 'linear-gradient(135deg, #E8F5E9 0%, #F1F8E9 100%)',
            borderRadius: 16,
            borderLeft: '6px solid #4CAF50',
          }}
        >
          <div style={{ fontSize: 28, color: '#4CAF50', fontWeight: 700, marginBottom: 8 }}>正确答案</div>
          <div style={{ fontSize: 28, color: '#2E7D32', fontWeight: 700, fontFamily: 'Georgia, serif' }}>
            2H₂ + O₂ → 2H₂O  （H: 4=4 · O: 2=2 ✓）
          </div>
        </div>
      </div>
    </AbsoluteFill>
  );
};

const OutroScene: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const scale = spring({ frame, fps, config: { damping: 12, stiffness: 110 } });
  const textOpacity = interpolate(frame, [30, 70], [0, 1], { extrapolateRight: 'clamp' });

  return (
    <AbsoluteFill style={{ justifyContent: 'center', alignItems: 'center', flexDirection: 'row', gap: 80 }}>
      <div style={{ fontSize: 220, transform: `scale(${scale})` }}>⚗️</div>
      <div style={{ opacity: textOpacity }}>
        <div style={{ fontSize: 100, fontWeight: 900, color: THEME.primary }}>掌握了！</div>
        <div style={{ fontSize: 30, color: '#555', marginTop: 16, letterSpacing: 4 }}>
          继续探索化学反应的奥妙
        </div>
      </div>
    </AbsoluteFill>
  );
};
